<template>
  <div class="gallery-sub-header-wrapper flex w-full absolute top-0 left-0">
    <div
      class="gallery-sub-header-left w-2/3 h-full ml-14 flex items-center gap-2 text-texts-standard-default text-40 pl-8"
      :class="{ 'counter-premium': isPremium }"
    >
      <slot></slot>
    </div>

    <div
      v-if="showRightPart"
      class="gallery-sub-header-right w-1/3 h-full"
    >
      <progress-status
        v-if="progressStatus > 0"
        :percent="progressStatus"
        class="progress-status-position"
      />

      <div
        v-if="showPictureCounter"
        class="picture-count-frame font-bold text-36 flex justify-between items-center mr-2"
        :class="{ skew: $isWsm, longer: locksCount > 0 }"
      >
        <div v-if="locksCount > 0" class="lock-container flexing">
          <span class="font-normal" :class="{ 'revert-skew': $isWsm }">{{ locksCount }}</span>
          <app-icon
            class="icon-count-frame"
            icon-name="lock-sm"
            :class="{ 'revert-skew': $isWsm }"
          />
        </div>

        <div class="flexing flex-grow">
          <span class="text-texts-standard-important" :class="{ 'revert-skew': $isWsm }">{{
            picturesCount.collectedCount
          }}</span>
          <span :class="{ 'revert-skew': $isWsm }">/{{ picturesCount.totalCount }}</span>
          <app-icon
            class="icon-count-frame"
            icon-name="gallery-images-md"
            :class="{ 'revert-skew': $isWsm }"
          />
        </div>
      </div>

      <div
        v-if="showCollectionName"
        class="picture-count-frame-dynamic font-bold text-36 flex justify-between items-center text-texts-standard-default"
      >
        {{ collectionName }}
      </div>

      <div
        v-else-if="isRepeatable || isSpecial"
        class="repeatable-pictures text-28 flex justify-between items-center text-texts-standard-default"
      >
        {{ isRepeatable ? $t('gallery.repeatablePicture') : $t('gallery.special') }}
        <app-icon
          v-if="isRepeatable"
          class="icon-info"
          icon-name="info-60"
          v-tippy="{
            theme: 'WSM',
            content: $t('gallery.repeatableInfo'),
            placement: 'top',
            textAlign: 'center',
          }"
        />
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, type PropType } from 'vue'
import { pathImages } from '@/globalVariables'
import ProgressStatus from './ProgressStatus.vue'

interface PicturesCount {
  collectedCount: number
  totalCount: number
}

interface ComponentData {
  pathImages: typeof pathImages
}

export default defineComponent({
  name: 'GallerySubHeader',

  components: { ProgressStatus },

  props: {
    showPictureCounter: {
      type: Boolean,
      default: false,
    },

    showCollectionName: {
      type: Boolean,
      default: false,
    },

    isRepeatable: {
      type: Boolean,
      default: false,
    },

    isSpecial: {
      type: Boolean,
      default: false,
    },

    collectionName: {
      type: String,
      default: '',
    },

    isPremium: {
      type: Boolean,
      default: false,
    },

    progressStatus: {
      type: Number,
      default: 0,
    },

    picturesCount: {
      type: Object as PropType<PicturesCount>,
      default: () => ({
        collectedCount: 0,
        totalCount: 0,
      }),
    },

    locksCount: {
      type: Number,
      default: 0,
    },
  },

  data(): ComponentData {
    return {
      pathImages,
    }
  },

  computed: {
    showRightPart(): boolean {
      return this.progressStatus > 0 ||
        this.showPictureCounter ||
        this.showCollectionName ||
        this.isRepeatable ||
        this.isSpecial
    }
  }
})
</script>

<style lang="scss" scoped>
.gallery-sub-header-wrapper {
  height: 4.25rem;

  .gallery-sub-header-left {
    margin-top: 1.25rem;
    background: url($path-images + 'gallery/BG_IMAGE_TITTLE.avif') 0 0 no-repeat;
    background-size: contain;

    &.counter-premium {
      background: url($path-images + 'gallery/BG_IMAGE_TITTLE_PURPLE.avif') 0 0 no-repeat;
      background-size: contain;
    }
  }

  .picture-count-frame {
    width: 10.625rem;
    height: 4.0625rem;
    box-shadow:
      inset -0.1875rem 0rem 2.5rem 0 rgba(6, 34, 55, 0.48),
      inset -0.01875rem -0.1875rem 2.5rem 0 rgba(6, 34, 55, 0.48);
    border-style: solid;
    border-width: 0.125rem;
    border-image-source: linear-gradient(to right, #fff284, #f6c717);
    border-image-slice: 1;
    line-height: 1;
    color: #fff;

    @if $isWsm {
      background: #0f3251;
    }
    @if $isSsm {
      background: #202544;
    }

    position: absolute;
    right: 1.75rem;
    top: 1.3125rem;

    &.longer {
      width: 17.5rem;
    }

    .lock-container {
      width: 6.875rem;
      height: 100%;
      @if $isWsm {
        background: #21517c;
      }
      @if $isSsm {
        background: #363e5d;
      }
    }

    .icon-count-frame {
      width: 2.875rem;
      height: 2.875rem;
      margin-left: 0.625rem;
    }
  }
  .picture-count-frame-dynamic {
    padding: 0 2rem;
    height: 4.0625rem;
    box-shadow:
      inset -0.1875rem 0rem 2.5rem 0 rgba(6, 34, 55, 0.48),
      inset -0.01875rem -0.1875rem 2.5rem 0 rgba(6, 34, 55, 0.48);
    border-style: solid;
    border-width: 0.125rem;
    border-image-source: linear-gradient(to right, #fff284, #f6c717);
    border-image-slice: 1;
    line-height: 1;
    @if $isWsm {
      background-color: #0f3251;
    }
    @if $isSsm {
      background-color: #363e5d;
    }

    position: absolute;
    right: 1.75rem;
    top: 1.3125rem;
  }

  .repeatable-pictures {
    height: 4.0625rem;
    line-height: 1;
    position: absolute;
    right: 1.75rem;
    top: 1.3125rem;

    .icon-info {
      margin-left: 1rem;
    }
  }
}
</style>
